import { Component, OnInit, ViewChild } from '@angular/core';
import { Log } from "ng2-logger";
import { environment } from "environments/environment";

import { TranslateParser, TranslateService } from "@ngx-translate/core";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { RoleService } from "app/core/services/role.service";
import { PermissionService } from "@app/authentication/services/permission.service";
import { InternalPositionService } from '@app/core/services/internal-position.service';

import { RolesModalComponent } from "./components/roles-modal/roles-modal.component";
import { TableBasedComponent } from "@app/shared/interface/TableBasedComponent";
import { TableConfig } from "app/shared/util/table-config.utils";

import { Permission } from "@models/permission.model";
import { UserRole } from "@models/role.model";
import { InternalPosition } from '@app/shared/models/internal-position.model';

@Component({
    selector: 'app-role',
    templateUrl: './roles.component.html',
    styleUrls: ['./roles.component.scss']
})

export class RolesComponent implements OnInit, TableBasedComponent<UserRole> {
    _logger = Log.create(RolesComponent.name);

    @ViewChild(RolesModalComponent, {static: true}) roleFormModal: RolesModalComponent;

    permissions: Permission[] = [];
    internalPositions: InternalPosition[] = [];
    roles: UserRole[] = [];
    selectedRole: UserRole = new UserRole();
    tableConfig = new TableConfig();
    isLoadingRoles = false;

    constructor(private roleService: RoleService,
                private toastr: ToastsManager,
                private translationService: TranslateService,
                private permissionService: PermissionService,
                private internalPositionService: InternalPositionService,
                private translateParser: TranslateParser) {

    }

    ngOnInit() {
        this._logger.data('init');
        this.getAll();

        this.getPermissions();
        this.getInternalPositions();
    }

    onTableRowClick(event) {
        this._logger.info('onTableRowClick: Opening modal with event', event);
        this.selectedRole = event;
        this.roleFormModal.open(event);
    }

    getAll() {
        this._logger.info('getAll(): Retrieving all roles from API');
        this.isLoadingRoles = true;
        this.roleService.getAllRoles(this.tableConfig).subscribe(
            (data: any) => {
                this._logger.data('getAll(): API returned', data.items);
                this.roles = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoadingRoles = false;
                this.tableConfig.pager.count = data.total;
            },
            (error: any) => {
                this._logger.error('getAll(): Error while retrieving roles', error);
                this.isLoadingRoles = false;
            }
        );
    }

    create(role: UserRole) {
        this._logger.info('create(): Role creation is disabled from the UI', role);
    }

    remove(role: UserRole) {
        this._logger.info('remove(): Role deletion is disabled from the UI', role);
    }

    update(roleUpdate) {
        this._logger.info('update(): Updating role', roleUpdate);
        this.isLoadingRoles = true;
        this.roleService.updateRole(roleUpdate.newRole).subscribe(
            (data: any) => {
                this._logger.info('update(): Role updated. API response is', data);
                this.getAll();
                this.toastr.success(this.buildUpdateMessage(roleUpdate.previousRoleName), null, {toastLife: environment.toasts.duration});
                this.isLoadingRoles = false;
                this.roleFormModal.close();
            },
            (error: any) => {
                this._logger.info('update(): Error whole updating role', error);
                this.isLoadingRoles = false;
            }
        );
    }

    onSortChange(tableState: TableConfig) {
        this._logger.info('onSortChange: ', tableState);
        this.getAll();
    }

    onFilter(tableConfig: TableConfig) {
        this._logger.info('onFilter: ', tableConfig);
        this.getAll();
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info('onFilter: ', tableConfig);
        this.getAll();
    }

    buildUpdateMessage(roleName) {
        const message = this.translationService.instant('admin.roles.toasts.updated');
        return this.translateParser.interpolate(message, {roleName: roleName});
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_ROLES');
    }

    private getPermissions() {
        this.permissionService.getAllPermissions().subscribe(
            (data: any) => {
                this.permissions = data.items;
            },
            (error: any) => {
            }
        );
    }

    private getInternalPositions() {
        this.internalPositionService.getAllInternalPositions().subscribe(
            (data: any) => {
                this.internalPositions = data.items;
            },
            (error: any) => {
            }
        );
    }
}
